
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CopyrightBundleLayout } from '../../components/CopyrightBundleLayout';
import { CheckCircle, Lock, Warning, HourglassEmpty, Check } from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';

export const CopyrightDisputeScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
      setSubmitting(true);
      setTimeout(() => {
          setSubmitting(false);
          setSubmitted(true);
      }, 2000);
  };

  return (
    <CopyrightBundleLayout>
        <div className="flex w-full max-w-[1100px] gap-8 h-full">
            {/* Main Document Preview */}
            <div className="flex-1 flex flex-col h-full">
                <div className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-6 rounded-t-xl border-x border-t">
                    <div className="flex gap-6">
                        <button className="text-xs font-bold text-primary border-b-2 border-primary h-14 px-1">{t('copyright.dispute.preview')}</button>
                        <button className="text-xs font-bold text-slate-400 h-14 px-1 hover:text-slate-600">{t('copyright.dispute.settings')}</button>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{t('copyright.dispute.language')}</span>
                        <span className="text-xs font-semibold text-slate-700">English (Global)</span>
                    </div>
                </div>

                <div className="flex-1 bg-white border-x border-b border-slate-200 p-12 overflow-y-auto custom-scrollbar rounded-b-xl shadow-sm">
                    <div className="max-w-[720px] mx-auto">
                        <div className="text-center mb-10">
                            <h2 className="font-serif text-2xl text-slate-900 mb-2">{t('copyright.dispute.title')}</h2>
                            <p className="text-[10px] font-mono text-slate-400 uppercase tracking-[0.2em]">{t('copyright.dispute.subtitle')}</p>
                        </div>

                        <div className="space-y-6 text-sm text-slate-700 leading-relaxed font-serif">
                            <div className="flex justify-between items-start mb-8 font-sans font-medium text-slate-500">
                                <div>
                                    <p>{t('copyright.dispute.to')}</p>
                                    <p className="text-primary bg-blue-50 px-1 inline-block">[Marketplace Name]</p>
                                </div>
                                <div className="text-right">
                                    <p>{t('copyright.dispute.date')} October 26, 2023</p>
                                    <p>{t('copyright.dispute.ref')} <span className="text-primary font-bold">[Evidence ID: #8832-LW]</span></p>
                                </div>
                            </div>

                            <section>
                                <h3 className="font-sans font-bold text-slate-900 mb-2 uppercase text-xs tracking-wider">{t('copyright.dispute.s1Title')}</h3>
                                <p>{t('copyright.dispute.s1Body')}</p>
                            </section>

                            <section>
                                <h3 className="font-sans font-bold text-slate-900 mb-2 uppercase text-xs tracking-wider">{t('copyright.dispute.s2Title')}</h3>
                                <p>{t('copyright.dispute.s2Body')}</p>
                                <div className="mt-2 p-3 bg-slate-50 border-l-2 border-primary font-mono text-xs text-blue-700">
                                    {t('copyright.dispute.s2Url')} <span className="font-bold">[Infringing URL]</span>
                                </div>
                                <p className="mt-3">{t('copyright.dispute.s2Desc')}</p>
                            </section>

                            <section>
                                <h3 className="font-sans font-bold text-slate-900 mb-2 uppercase text-xs tracking-wider">{t('copyright.dispute.s3Title')}</h3>
                                <p>{t('copyright.dispute.s3Body')}</p>
                                <ul className="list-disc ml-5 mt-2 space-y-1">
                                    <li><span className="font-sans font-semibold text-slate-800">{t('copyright.dispute.s3Item1')}</span>{t('copyright.dispute.s3Item1Desc')}</li>
                                    <li><span className="font-sans font-semibold text-slate-800">{t('copyright.dispute.s3Item2')}</span>{t('copyright.dispute.s3Item2Desc')}</li>
                                </ul>
                            </section>

                            <section>
                                <h3 className="font-sans font-bold text-slate-900 mb-2 uppercase text-xs tracking-wider">{t('copyright.dispute.s4Title')}</h3>
                                <p>{t('copyright.dispute.s4Body')}</p>
                            </section>

                            <div className="pt-10">
                                <div className="w-48 border-b border-slate-900 mb-2 h-12 flex items-end">
                                    <span className="font-serif italic text-slate-400 text-lg">OneClickSell Legal</span>
                                </div>
                                <p className="font-bold text-slate-900">{t('copyright.dispute.signature')}</p>
                                <p className="text-xs text-slate-500">{t('copyright.dispute.division')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sidebar Controls */}
            <aside className="w-[300px] flex flex-col gap-6">
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                    <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">{t('copyright.dispute.sidebar.verifStatus')}</h4>
                    <ul className="space-y-3">
                        <li className="flex items-start gap-2">
                            <CheckCircle className="text-emerald-500" style={{ fontSize: 16, marginTop: 2 }} />
                            <div className="flex-1">
                                <p className="text-xs font-semibold text-slate-700">{t('copyright.dispute.sidebar.link')}</p>
                                <p className="text-[10px] text-slate-400">{t('copyright.dispute.sidebar.linkDesc')}</p>
                            </div>
                        </li>
                        <li className="flex items-start gap-2">
                            <CheckCircle className="text-emerald-500" style={{ fontSize: 16, marginTop: 2 }} />
                            <div className="flex-1">
                                <p className="text-xs font-semibold text-slate-700">{t('copyright.dispute.sidebar.psd')}</p>
                                <p className="text-[10px] text-slate-400">{t('copyright.dispute.sidebar.psdDesc')}</p>
                            </div>
                        </li>
                        <li className="flex items-start gap-2">
                            <Warning className="text-orange-400" style={{ fontSize: 16, marginTop: 2 }} />
                            <div className="flex-1">
                                <p className="text-xs font-semibold text-slate-700">{t('copyright.dispute.sidebar.evidence')}</p>
                                <p className="text-[10px] text-orange-500">{t('copyright.dispute.sidebar.missing')}</p>
                            </div>
                        </li>
                    </ul>
                </div>

                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                    <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">{t('copyright.dispute.sidebar.prob')}</h4>
                    <div className="bg-orange-50 rounded-xl p-6 border border-orange-100 text-center mb-6 relative overflow-hidden">
                        <div className="relative inline-flex items-center justify-center mb-2 w-24 h-24">
                            <svg className="w-full h-full" viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }}>
                                <circle className="text-orange-100" cx="50" cy="50" fill="transparent" r="40" stroke="currentColor" strokeWidth="8"></circle>
                                <circle className="text-orange-500" cx="50" cy="50" fill="transparent" r="40" stroke="currentColor" strokeDasharray="251.2" strokeDashoffset="138" strokeLinecap="round" strokeWidth="8"></circle>
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className="text-2xl font-bold text-orange-700">45%</span>
                            </div>
                        </div>
                        <p className="text-[11px] font-bold text-orange-600 uppercase tracking-wide">{t('copyright.dispute.sidebar.lowConf')}</p>
                    </div>

                    <div className="mb-2">
                        <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">{t('copyright.dispute.sidebar.guide')}</h4>
                        <div className="space-y-3">
                            <div className="p-3 bg-white border border-slate-200 rounded-lg shadow-sm">
                                <div className="flex justify-between items-start mb-2">
                                    <div>
                                        <p className="text-[11px] font-bold text-slate-700">{t('copyright.dispute.sidebar.scan')}</p>
                                        <p className="text-[10px] text-orange-600 font-medium">{t('copyright.dispute.sidebar.boost30')}</p>
                                    </div>
                                    <button onClick={() => navigate('/copyright/watermark')} className="px-2 py-1 bg-primary text-[10px] font-bold text-white rounded hover:bg-blue-700 transition-colors">{t('copyright.dispute.sidebar.complete')}</button>
                                </div>
                                <div className="w-full bg-slate-100 h-1 rounded-full overflow-hidden">
                                    <div className="bg-orange-400 h-full w-[0%]"></div>
                                </div>
                            </div>
                            <div className="p-3 bg-white border border-slate-200 rounded-lg shadow-sm">
                                <div className="flex justify-between items-start mb-2">
                                    <div>
                                        <p className="text-[11px] font-bold text-slate-700">{t('copyright.dispute.sidebar.proof')}</p>
                                        <p className="text-[10px] text-orange-600 font-medium">{t('copyright.dispute.sidebar.boost22')}</p>
                                    </div>
                                    <button onClick={() => navigate('/copyright/blockchain')} className="px-2 py-1 bg-primary text-[10px] font-bold text-white rounded hover:bg-blue-700 transition-colors">{t('copyright.dispute.sidebar.complete')}</button>
                                </div>
                                <div className="w-full bg-slate-100 h-1 rounded-full overflow-hidden">
                                    <div className="bg-orange-400 h-full w-[0%]"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-auto">
                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight mb-2">{t('copyright.dispute.sidebar.autoFiling')}</p>
                        <p className="text-[10px] text-slate-500 italic leading-snug mb-3">{t('copyright.dispute.sidebar.filingNote')}</p>
                        <button
                            onClick={handleSubmit}
                            disabled={submitting || submitted}
                            className={`w-full py-2 border text-xs font-bold rounded-lg flex items-center justify-center gap-2 transition-all ${submitted ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white border-slate-200 text-slate-400 hover:text-slate-600'}`}
                        >
                            {submitting ? <HourglassEmpty style={{ fontSize: 14 }} className="animate-spin" /> : submitted ? <Check style={{ fontSize: 14 }} /> : <Lock style={{ fontSize: 14 }} />}
                            {submitting ? t('copyright.dispute.sidebar.processing') : submitted ? t('copyright.dispute.sidebar.submitted') : t('copyright.dispute.sidebar.autoSubmit')}
                        </button>
                    </div>
                </div>
            </aside>
        </div>
    </CopyrightBundleLayout>
  );
};
